import React from 'react';
/**
 * `RenewalsPageInfo` renders the desktop sidebar content for the renewals
 * journey.
 *
 * Behaviour:
 * - Varies "Just so you know" text and whether to show "Reasons to extend"
 *   based on `currentPath`.
 * - For confirmation, shows a background image panel.
 * - Renders useful documents and (for call centre) can include product
 *   documents from the journey state.
 */

import './RenewalsPageInfo.scss';
import ReasonsToExtend from './ReasonsToExtend';
import JustSoYouKnow from './JustSoYouKnow';
import { Journey, PageAbsolutePath } from '../../../consts/Common';
import { DocumentView } from '../../common/Constants';
import { DocumentTreeItem, JourneyState } from '../../common/CommonTypes';
import UsefulDocuments from '../UsefulDocuments';
import { useAppSelector } from '../../../app-state/Hooks';

export type RenewalsPageInfoProps = {
  renewalsJourney: JourneyState;
  currentPath: string;
  setExpandedDocIds: (docIds: string[]) => void;
};

const RenewalsPageInfo = ({
  renewalsJourney,
  currentPath,
  setExpandedDocIds
}: RenewalsPageInfoProps): JSX.Element => {
  const cms = useAppSelector((s) => s.content);
  const journeyType = useAppSelector((s) => s.appConfig.journeyType);

  const isCallCentre = journeyType === Journey.CallCentre;
  let justSoYouKnowText = '';
  let documents: DocumentTreeItem[] = renewalsJourney.documents;
  let showReasonsToExtend = false;

  if (currentPath === PageAbsolutePath.Renewals.Confirmation) {
    return (
      <div className='desktop-content-full'>
        <img
          className='landing-image'
          src={cms.confirmationPage.backgroundImageUrl}
          alt={cms.confirmationPage.backgroundImageAltText}
        />
      </div>
    );
  }

  switch (currentPath) {
    case PageAbsolutePath.Renewals.PersonalDetails:
      justSoYouKnowText = cms.customerPage.justSoYouKnow;
      documents = isCallCentre ? renewalsJourney.documents : [];
      showReasonsToExtend = true;
      break;
    case PageAbsolutePath.Renewals.VehicleDetails:
      justSoYouKnowText = cms.vehiclePage.justSoYouKnow;
      showReasonsToExtend = true;
      break;
    case PageAbsolutePath.Renewals.Quote:
      justSoYouKnowText = cms.quotePage.justSoYouKnow;
      showReasonsToExtend = true;
      break;
    case PageAbsolutePath.Renewals.Summary:
      justSoYouKnowText = cms.summaryPage.justSoYouKnow;
      showReasonsToExtend = true;
      break;
    case PageAbsolutePath.Renewals.DirectDebit:
      justSoYouKnowText = cms.directDebitPage.justSoYouKnow;
      showReasonsToExtend = true;
      break;
    case PageAbsolutePath.Renewals.SinglePayment:
      justSoYouKnowText = cms.singlePaymentPage.justSoYouKnow;
      showReasonsToExtend = true;
      break;
    default:
  }

  if (isCallCentre) {
    showReasonsToExtend = false;
  }

  return (
    <div className='desktop-content page-info-container sticky-content'>
      {showReasonsToExtend && (
        <ReasonsToExtend reasonsToExtend={cms.reasonsToExtend} />
      )}
      {justSoYouKnowText !== '' && <JustSoYouKnow text={justSoYouKnowText} />}
      <UsefulDocuments
        documents={documents}
        cmsDocuments={isCallCentre ? [] : cms.usefulDocuments}
        view={DocumentView.Desktop}
        expandedDocIds={renewalsJourney.expandedDocIds}
        setExpandedDocIds={setExpandedDocIds}
        showDivider={false}
      />
    </div>
  );
};

export default RenewalsPageInfo;
